import { useMemo } from "react";
import { clsx } from "clsx";
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import csharp from "highlight.js/lib/languages/csharp";
import javaGrammar from "highlight.js/lib/languages/java";
import javascript from "highlight.js/lib/languages/javascript";
import phpGrammar from "highlight.js/lib/languages/php";
import pythonGrammar from "highlight.js/lib/languages/python";
// Global by necessity: highlight.js writes fixed `hljs-*` class names into the markup below,
// which a hashed CSS module could never match. Every rule in the theme is anchored on one of
// those classes, so it only ever paints highlighted code.
import "./HttpRequestSnippetHighlight.less";
import { csharpHttpclient } from "@scalar/snippetz/plugins/csharp/httpclient";
import { javaNethttp } from "@scalar/snippetz/plugins/java/nethttp";
import { jsFetch } from "@scalar/snippetz/plugins/js/fetch";
import { phpCurl } from "@scalar/snippetz/plugins/php/curl";
import { pythonRequests } from "@scalar/snippetz/plugins/python/requests";
import { shellCurl } from "@scalar/snippetz/plugins/shell/curl";
import type { Request as HarRequest } from "har-format";
import { CopyButton } from "@/components/CopyButton";
import { Tabs } from "@/primitives/Tabs";
import styles from "./HttpRequestSnippetTabs.module.css";

const TARGETS = {
	curl: { label: "cURL", plugin: shellCurl, grammar: bash },
	javascript: { label: "JavaScript", plugin: jsFetch, grammar: javascript },
	python: { label: "Python", plugin: pythonRequests, grammar: pythonGrammar },
	java: { label: "Java", plugin: javaNethttp, grammar: javaGrammar },
	csharp: { label: "C#", plugin: csharpHttpclient, grammar: csharp },
	php: { label: "PHP", plugin: phpCurl, grammar: phpGrammar },
};

export type SnippetTarget = keyof typeof TARGETS;

export const SNIPPET_TARGETS = Object.keys(TARGETS) as SnippetTarget[];

// Registered under the target id, so highlighting never needs a second name for a language.
Object.entries(TARGETS).forEach(([id, { grammar }]) => {
	if (!hljs.getLanguage(id)) {
		hljs.registerLanguage(id, grammar);
	}
});

export interface Secret {
	value: string;
	label: string;
}

/**
 * Marks a value that must not be read off the screen. The snippet shows `{{label}}` in its
 * place; the copy button puts the real value on the clipboard.
 */
export function secret(value: string, label = "secret"): Secret {
	return { value, label };
}

/** Plain text, a secret, or a mix — e.g. `["Bearer ", secret(token)]`. */
export type RequestValue = string | Secret | (string | Secret)[];

/** One HTTP request, described once and rendered into every language. */
export interface HttpRequestDefinition {
	method?: string;
	url: RequestValue;
	headers?: Record<string, RequestValue>;
	query?: Record<string, RequestValue>;
	/** An `application/x-www-form-urlencoded` body. Ignored when `body` is set too. */
	form?: Record<string, RequestValue>;
	/** Any other body, e.g. `{mimeType: "application/json", text: JSON.stringify(payload)}`. */
	body?: { mimeType: string; text: RequestValue };
}

const HIDDEN_PATTERN = /timrSecretStart([A-Za-z0-9_]+)timrSecretEnd/g;

function resolve(value: RequestValue, reveal: boolean): string {
	if (Array.isArray(value)) {
		return value.map((part) => resolve(part, reveal)).join("");
	}
	if (typeof value === "string") {
		return value;
	}
	return reveal ? value.value : `timrSecretStart${value.label}timrSecretEnd`;
}

function resolveEntries(entries: Record<string, RequestValue> | undefined, reveal: boolean): { name: string; value: string }[] {
	return Object.entries(entries ?? {}).map(([name, value]) => ({ name, value: resolve(value, reveal) }));
}

function toHarRequest(request: HttpRequestDefinition, reveal: boolean): Partial<HarRequest> {
	const form = request.form && resolveEntries(request.form, reveal);

	return {
		method: (request.method ?? "GET").toUpperCase(),
		url: resolve(request.url, reveal),
		headers: resolveEntries(request.headers, reveal),
		queryString: resolveEntries(request.query, reveal),
		...(form && {
			postData: {
				mimeType: "application/x-www-form-urlencoded",
				params: form,
				text: form.map(({ name, value }) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`).join("&"),
			} as HarRequest["postData"],
		}),
		...(request.body && {
			postData: { mimeType: request.body.mimeType, text: resolve(request.body.text, reveal) },
		}),
	};
}

// does the code highlighting with highlight.js
function toSnippetHtml(code: string, target: SnippetTarget): string {
	return hljs
		.highlight(code, { language: target })
		.value.replace(
			HIDDEN_PATTERN,
			(match, label: string) =>
				`<span class="${styles.secret}" title="${messages["httpRequestSnippet.secret.hidden"]}">{{${label}}}</span>`,
		);
}

export interface HttpRequestSnippetTabsProps {
	request: HttpRequestDefinition;
	targets?: SnippetTarget[];
	defaultTarget?: SnippetTarget;
	className?: string;
}

// the actual component
export function HttpRequestSnippetTabs({
	request,
	targets = SNIPPET_TARGETS,
	defaultTarget,
	className,
}: HttpRequestSnippetTabsProps) {
	const snippets = useMemo(() => {
		const shownRequest = toHarRequest(request, false);
		const copiedRequest = toHarRequest(request, true);

		return targets.map((target) => {
			const { label, plugin } = TARGETS[target];
			return {
				target,
				label,
				copied: plugin.generate(copiedRequest),
				html: toSnippetHtml(plugin.generate(shownRequest), target),
			};
		});
	}, [request, targets]);

	return (
		<Tabs.Root className={clsx(styles.root, className)} defaultValue={defaultTarget ?? targets[0]}>
			<Tabs.List className={styles.list}>
				{snippets.map(({ target, label }) => (
					<Tabs.Tab key={target} value={target}>
						{label}
					</Tabs.Tab>
				))}
			</Tabs.List>
			{snippets.map(({ target, copied, html }) => (
				<Tabs.Panel key={target} value={target} className={styles.panel}>
					<pre className={styles.pre}>
						<code className={styles.code} dangerouslySetInnerHTML={{ __html: html }} />
					</pre>
					<CopyButton text={copied} className={styles.copy} />
				</Tabs.Panel>
			))}
		</Tabs.Root>
	);
}
